import { useRef, useCallback, useState } from 'react';
import { parseCdxml, parseMolV2000, parseSmiles, writeMolV2000 } from '@kendraw/io';
import type { Atom, Bond, SceneStore } from '@kendraw/scene';
import { FEATURE_FLAGS } from './config/feature-flags';
import { getKetcherBridgeState } from './canvas-ketcher';
import { setGraphicOverlays, setCdxmlDocumentSettings } from './graphic-overlays';

interface ImportDialogProps {
  store: SceneStore;
  onClose: () => void;
}

export function ImportDialog({ store, onClose }: ImportDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [status, setStatus] = useState('');

  const importContent = useCallback(
    async (content: string, filename: string) => {
      const ext = filename.toLowerCase().split('.').pop() ?? '';
      let atoms = 0;
      let bonds = 0;

      // Ketcher mode: parse locally, then hand the resulting MOL to the
      // Ketcher editor via setMolecule. CDXML-only artefacts (arrows,
      // annotations, graphic overlays) are dropped in this mode — they
      // will be handled in Session B.
      if (FEATURE_FLAGS.useKetcher) {
        const ketcher = getKetcherBridgeState().ketcher;
        if (!ketcher) {
          setStatus('Ketcher editor not ready yet.');
          return;
        }
        try {
          const { mol, atoms: a, bonds: b } = parseToMol(content, ext);
          if (!mol) {
            setStatus('Could not parse file. Supported: CDXML, MOL, SDF, SMILES');
            return;
          }
          await ketcher.setMolecule(mol);
          setStatus(`Imported ${a} atoms, ${b} bonds`);
          setTimeout(onClose, 800);
        } catch (err) {
          setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
        return;
      }

      try {
        if (ext === 'cdxml' || content.includes('<CDXML') || content.includes('<cdxml')) {
          const result = parseCdxml(content);
          for (const a of result.atoms) store.dispatch({ type: 'add-atom', atom: a });
          for (const b of result.bonds) store.dispatch({ type: 'add-bond', bond: b });
          for (const ar of result.arrows) store.dispatch({ type: 'add-arrow', arrow: ar });
          for (const an of result.annotations)
            store.dispatch({ type: 'add-annotation', annotation: an });
          if (result.graphics.length > 0) setGraphicOverlays(result.graphics);
          setCdxmlDocumentSettings(result.documentSettings);
          atoms = result.atoms.length;
          bonds = result.bonds.length;
        } else if (ext === 'mol' || ext === 'sdf' || content.includes('V2000')) {
          // SDF can have multiple molecules separated by $$$$
          const blocks = content.split('$$$$').filter((b) => b.trim());
          let molGroupOffsetX = 0;
          for (const block of blocks) {
            const result = parseMolV2000(block);
            if (result.atoms.length === 0) continue;

            // Center molecule on canvas viewport
            let cx = 0;
            let cy = 0;
            for (const a of result.atoms) {
              cx += a.x;
              cy += a.y;
            }
            cx /= result.atoms.length;
            cy /= result.atoms.length;

            // Find free space to the right of existing atoms
            const page = store.getState().pages[store.getState().activePageIndex];
            let baseX = 400 + molGroupOffsetX;
            if (page) {
              const existing = Object.values(page.atoms);
              if (existing.length > 0) {
                let maxX = -Infinity;
                for (const ea of existing) {
                  if (ea.x > maxX) maxX = ea.x;
                }
                baseX = Math.max(baseX, maxX + 80);
              }
            }
            const offsetX = baseX - cx;
            const offsetY = 300 - cy;

            for (const a of result.atoms) {
              store.dispatch({
                type: 'add-atom',
                atom: { ...a, x: a.x + offsetX, y: a.y + offsetY },
              });
            }
            for (const b of result.bonds) store.dispatch({ type: 'add-bond', bond: b });
            atoms += result.atoms.length;
            bonds += result.bonds.length;
            molGroupOffsetX += 200; // space out multiple molecules from SDF
          }
        } else if (ext === 'smi' || ext === 'smiles') {
          // One SMILES per line
          const lines = content
            .split('\n')
            .map((l) => l.trim().split(/\s/)[0])
            .filter((l): l is string => !!l && l.length > 0);
          for (const smi of lines) {
            const result = parseSmiles(smi);
            for (const a of result.atoms) store.dispatch({ type: 'add-atom', atom: a });
            for (const b of result.bonds) store.dispatch({ type: 'add-bond', bond: b });
            atoms += result.atoms.length;
            bonds += result.bonds.length;
          }
        } else {
          // Try SMILES as fallback
          const result = parseSmiles(content.trim());
          if (result.atoms.length > 0) {
            for (const a of result.atoms) store.dispatch({ type: 'add-atom', atom: a });
            for (const b of result.bonds) store.dispatch({ type: 'add-bond', bond: b });
            atoms = result.atoms.length;
            bonds = result.bonds.length;
          } else {
            setStatus('Could not parse file. Supported: CDXML, MOL, SDF, SMILES');
            return;
          }
        }

        setStatus(`Imported ${atoms} atoms, ${bonds} bonds`);
        setTimeout(onClose, 800);
      } catch (err) {
        setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    },
    [store, onClose],
  );

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          void importContent(reader.result, file.name);
        }
      };
      reader.readAsText(file);
    },
    [importContent],
  );

  const handlePaste = useCallback(() => {
    if (text.trim()) {
      void importContent(text.trim(), 'paste.txt');
    }
  }, [text, importContent]);

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <h3 style={titleStyle}>Import File</h3>

        {/* File picker */}
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => fileRef.current?.click()} style={btnStyle}>
            Choose File...
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".cdxml,.mol,.sdf,.smi,.smiles,.txt"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
          <span style={{ fontSize: 10, color: 'var(--kd-color-text-muted)', marginLeft: 8 }}>
            CDXML, MOL, SDF, SMILES
          </span>
        </div>

        {/* Or paste content */}
        <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--kd-color-text-muted)' }}>
          Or paste CDXML / MOL / SMILES content:
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste file content or SMILES here..."
          style={textareaStyle}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            style={{
              fontSize: 11,
              color: status.startsWith('Error')
                ? 'var(--kd-color-danger)'
                : 'var(--kd-color-success)',
            }}
          >
            {status}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={onClose}
              style={{ ...btnStyle, background: 'var(--kd-color-surface)' }}
            >
              Cancel
            </button>
            <button onClick={handlePaste} disabled={!text.trim()} style={btnStyle}>
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--kd-color-overlay-scrim)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: 80,
  zIndex: 100,
};

const panelStyle: React.CSSProperties = {
  width: 440,
  background: 'var(--kd-color-modal-bg)',
  backdropFilter: 'blur(20px)',
  border: '1px solid var(--kd-color-modal-border)',
  borderRadius: 12,
  padding: 16,
  boxShadow: 'var(--kd-shadow-modal)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 12,
  color: 'var(--kd-color-text-primary)',
};

const btnStyle: React.CSSProperties = {
  padding: '6px 14px',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--kd-color-accent)',
  color: 'var(--kd-color-text-inverse)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  height: 120,
  padding: 8,
  fontSize: 11,
  fontFamily: 'var(--kd-font-mono)',
  background: 'var(--kd-color-surface)',
  color: 'var(--kd-color-text-primary)',
  border: '1px solid var(--kd-color-border)',
  borderRadius: 6,
  resize: 'vertical',
  marginBottom: 10,
  outline: 'none',
};

interface ParsedMol {
  mol: string;
  atoms: number;
  bonds: number;
}

function parseToMol(content: string, ext: string): ParsedMol {
  // CDXML in Ketcher mode falls back to atoms + bonds only.
  if (ext === 'cdxml' || content.includes('<CDXML') || content.includes('<cdxml')) {
    const result = parseCdxml(content);
    const mol = writeMolV2000(result.atoms as Atom[], result.bonds as Bond[]);
    return { mol, atoms: result.atoms.length, bonds: result.bonds.length };
  }
  if (ext === 'mol' || ext === 'sdf' || content.includes('V2000')) {
    // Pass the raw MOL through — Ketcher accepts it directly.
    const block = content.split('$$$$')[0] ?? content;
    const probe = parseMolV2000(block);
    return { mol: block, atoms: probe.atoms.length, bonds: probe.bonds.length };
  }
  if (ext === 'smi' || ext === 'smiles') {
    const first = content
      .split('\n')
      .map((l) => l.trim().split(/\s/)[0])
      .find((l): l is string => !!l && l.length > 0);
    if (!first) return { mol: '', atoms: 0, bonds: 0 };
    const result = parseSmiles(first);
    const mol = writeMolV2000(result.atoms, result.bonds);
    return { mol, atoms: result.atoms.length, bonds: result.bonds.length };
  }
  // Fallback: try SMILES.
  const result = parseSmiles(content.trim());
  if (result.atoms.length === 0) return { mol: '', atoms: 0, bonds: 0 };
  const mol = writeMolV2000(result.atoms, result.bonds);
  return { mol, atoms: result.atoms.length, bonds: result.bonds.length };
}
